/**
 * The hero take, pure — ADR-033 phase 6.
 *
 * The viewport hands over square RGBA pixels read back from an offscreen
 * target rendered through the SAME lens as the screen (tone map, sRGB — the
 * renderer's output-target seam); this module turns them into what the
 * package folder ships: a straight-alpha 1:1 PNG, transparent where nothing
 * was drawn (design note F4: World frames the cut-out, so a re-theme never
 * needs re-shooting). It also measures the take — where the subject sits in
 * the frame — so the status line and the smoke can say something more than
 * "captured".
 *
 * Orientation is the backend's: WebGL's readPixels hands rows bottom-first,
 * WebGPU's buffer copy top-first; the viewport says which and this flips.
 *
 * Depends on: fast-png. Depended on by: the viewport's capture caller (main),
 * the smoke, tests.
 */
import { decode as decodePng, encode as encodePng } from 'fast-png';
import { THUMBNAIL_MAX_BYTES } from '@harryhausen/kernel';

export { HERO_SIZE } from './hero-size.js';

export interface HeroPixels {
  readonly rgba: Uint8Array;
  readonly size: number;
  /** True when rows arrive bottom-first (WebGL readPixels). */
  readonly flipY: boolean;
}

/** The take's frame facts: the opaque bounding box in top-down pixel rows, or null when nothing was drawn. */
export interface HeroFacts {
  readonly size: number;
  readonly opaque: number;
  readonly box: { readonly top: number; readonly bottom: number; readonly left: number; readonly right: number } | null;
  /** The subject's height as a fraction of the frame — the number an author reads to judge the framing. */
  readonly fill: number;
}

/** Rows top-first, as the PNG will carry them (alpha still as the target holds it — see `unpremultiply`). */
export function heroRowsTopDown(px: HeroPixels): Uint8Array {
  const { rgba, size, flipY } = px;
  if (rgba.length !== size * size * 4) throw new Error(`hero pixels: ${rgba.length} bytes is not ${size}×${size} RGBA`);
  if (!flipY) return rgba;
  const out = new Uint8Array(rgba.length);
  const row = size * 4;
  for (let y = 0; y < size; y += 1) out.set(rgba.subarray(y * row, (y + 1) * row), (size - 1 - y) * row);
  return out;
}

/**
 * The target holds PREMULTIPLIED colour at the silhouette: the MSAA resolve
 * averages an opaque sample with the transparent-black clear, so RGB scales
 * with coverage; a transparent material writes the same way. PNG RGBA is
 * straight alpha — encoded as read, World's compositor would multiply the
 * edge by alpha a second time and draw a dark fringe around the cut-out
 * (Codex P1, #154). Divide back where alpha is nonzero; in place.
 */
export function unpremultiply(rgba: Uint8Array): Uint8Array {
  for (let i = 0; i < rgba.length; i += 4) {
    const a = rgba[i + 3]!;
    if (a === 0 || a === 255) continue;
    rgba[i] = Math.min(255, Math.round((rgba[i]! * 255) / a));
    rgba[i + 1] = Math.min(255, Math.round((rgba[i + 1]! * 255) / a));
    rgba[i + 2] = Math.min(255, Math.round((rgba[i + 2]! * 255) / a));
  }
  return rgba;
}

/**
 * The square capture's vertical FOV. The crop guide is the viewport's
 * INSCRIBED square: on a landscape viewport it spans the height, so the
 * live vertical FOV is the crop; on a PORTRAIT viewport it spans the width,
 * so the square must keep the live HORIZONTAL extent — tan(v'/2) =
 * tan(v/2) · aspect (Codex P2, #154). Degrees in, degrees out.
 */
export function squareFovDeg(fovDeg: number, aspect: number): number {
  if (!(aspect > 0) || aspect >= 1) return fovDeg;
  const half = Math.tan((fovDeg * Math.PI) / 360) * aspect;
  return (Math.atan(half) * 360) / Math.PI;
}

export function heroFacts(topDown: Uint8Array, size: number): HeroFacts {
  let top = size;
  let bottom = -1;
  let left = size;
  let right = -1;
  let opaque = 0;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (topDown[(y * size + x) * 4 + 3]! > 0) {
        opaque += 1;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  const box = bottom < 0 ? null : { top, bottom, left, right };
  return { size, opaque, box, fill: box === null ? 0 : (box.bottom - box.top + 1) / size };
}

export interface HeroTake {
  readonly png: Uint8Array;
  readonly facts: HeroFacts;
}

/** Pixels → the PNG the folder ships, plus what it measured. A frame with nothing drawn is refused — a blank card is not a take. */
export function heroTakeFromPixels(px: HeroPixels): HeroTake {
  const topDown = unpremultiply(heroRowsTopDown(px).slice());
  const facts = heroFacts(topDown, px.size);
  if (facts.box === null) throw new Error('hero take: nothing was drawn — the frame is fully transparent');
  const png = encodePng({ width: px.size, height: px.size, data: topDown, channels: 4, depth: 8 });
  return { png, facts };
}

/**
 * The record's card: the starred take downscaled to `size`² (World sized
 * the card at 512²; the extras tenant refuses over 200 KB) as a PNG data
 * URI. A box filter over whole pixels, alpha-weighted so a cut-out's edge
 * does not darken toward the transparent clear (straight alpha in, straight
 * alpha out). The factor must divide the take's side.
 */
export function heroThumbnailDataUri(takePng: Uint8Array, size = 512, maxBytes = THUMBNAIL_MAX_BYTES): string {
  const src = decodePng(takePng);
  if (src.width !== src.height) throw new Error(`hero thumbnail: the take is ${src.width}×${src.height}, not square`);
  if (src.channels !== 4 || src.depth !== 8) throw new Error(`hero thumbnail: the take is ${src.channels}-channel ${src.depth}-bit, not RGBA8`);
  // The extras tenant refuses a card over the byte ceiling, and a detailed
  // 8K-class character can push a lossless 512² past it (the boot subject
  // measures 107 KB; the ceiling is 200 KB). The author cannot "capture at
  // 512²" — every take is 1024² — so the card steps down by halves until it
  // fits (Codex P1, #156 round two). Refused only when 128² will not.
  let side = size;
  for (;;) {
    if (src.width % side !== 0) throw new Error(`hero thumbnail: ${src.width} is not a multiple of ${side}`);
    const png = downscalePng(src.data as Uint8Array, src.width, side);
    if (png.length <= maxBytes) return `data:image/png;base64,${base64Of(png)}`;
    if (side <= 128) throw new Error(`hero thumbnail: ${png.length} bytes at ${side}² is over the ${maxBytes}-byte ceiling — a simpler frame is needed`);
    side /= 2;
  }
}

function downscalePng(data: Uint8Array, width: number, size: number): Uint8Array {
  const f = width / size;
  const out = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let dy = 0; dy < f; dy += 1) {
        for (let dx = 0; dx < f; dx += 1) {
          const i = ((y * f + dy) * width + (x * f + dx)) * 4;
          const alpha = data[i + 3]!;
          r += data[i]! * alpha;
          g += data[i + 1]! * alpha;
          b += data[i + 2]! * alpha;
          a += alpha;
        }
      }
      const o = (y * size + x) * 4;
      if (a > 0) {
        out[o] = Math.round(r / a);
        out[o + 1] = Math.round(g / a);
        out[o + 2] = Math.round(b / a);
      }
      out[o + 3] = Math.round(a / (f * f));
    }
  }
  return encodePng({ width: size, height: size, data: out, channels: 4, depth: 8 }, { zlib: { level: 9 } });
}

function base64Of(bytes: Uint8Array): string {
  if (typeof btoa === 'function') {
    let s = '';
    for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(s);
  }
  return Buffer.from(bytes).toString('base64');
}

/** `take-01.png`, `take-02.png`, … — the first name not already taken (a lifted take's number is not reused in a session; the count only grows). */
export function nextTakeName(taken: readonly string[]): string {
  let n = 1;
  for (const t of taken) {
    const m = /^take-(\d+)\.png$/.exec(t);
    if (m !== null) n = Math.max(n, Number(m[1]) + 1);
  }
  return `take-${String(n).padStart(2, '0')}.png`;
}

/** One line for the status bar. */
export function heroTakeLine(name: string, facts: HeroFacts): string {
  return `hero: ${name} · ${facts.size}² · subject fills ${(facts.fill * 100).toFixed(0)}% of the height`;
}
